import {useEffect, useState} from 'react';
import {Alert, Button, Col, Input, Row, Spin, Typography, message} from 'antd';
import {useHistory, useParams} from 'react-router-dom';
import {NewLevel} from '@PikaDoApp/npm-pikado-logic';
import {LevelsService} from './LevelsService';
import {fetchUserAttributes} from 'aws-amplify/auth';

const levelsService = new LevelsService();

export const LevelCreateEdit = () => {
  const history = useHistory();
  const {Title, Text} = Typography;
  const params: {levelId?: string} = useParams();

  // Determine if we're in edit mode based on presence of levelId
  const isEditMode = !!params.levelId;

  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [level, setLevel] = useState<NewLevel | null>(null);
  const [levelName, setLevelName] = useState<string>('');

  // Fetch level data in edit mode only
  useEffect(() => {
    if (!isEditMode) return;

    const fetchLevel = async () => {
      setIsLoadingData(true);
      setIsError(false);
      try {
        console.log(
          '🔍 [LEVEL-NEW-PACKAGE] Fetching level with ID:',
          params.levelId,
        );
        const fetchedLevel = await levelsService.getNewLevelById(
          params.levelId!,
        );

        if (fetchedLevel) {
          setLevel(fetchedLevel);
          setLevelName(fetchedLevel.name || '');
        } else {
          setIsError(true);
          message.error('Level not found');
        }
      } catch (error) {
        console.error(
          '🔍 [LEVEL-NEW-PACKAGE] Edit page - error loading level:',
          error,
        );
        setIsError(true);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchLevel();
  }, [isEditMode, params.levelId]);

  const handleSubmit = async () => {
    // Validate required fields
    if (!levelName || levelName.trim() === '') {
      message.error('Please enter a level name');
      return;
    }

    setIsSaving(true);

    try {
      if (isEditMode) {
        // Update existing level
        const updateInput = {
          id: params.levelId!,
          name: levelName.trim(),
        };

        console.log(
          '🔍 [LEVEL-NEW-PACKAGE] Updating level with input:',
          updateInput,
        );
        await levelsService.updateNewLevel(updateInput);
        message.success('Level updated successfully!');
      } else {
        // Create new level
        const currentUser = await fetchUserAttributes();
        const userId = currentUser.sub || 'unknown';

        const levelInput = {
          name: levelName.trim(),
          createdBy: userId,
        };

        console.log(
          '🔍 [LEVEL-NEW-PACKAGE] Creating level with input:',
          levelInput,
        );
        await levelsService.createNewLevel(levelInput);
        message.success('Level created successfully!');
      }

      history.push('/levels');
    } catch (error) {
      console.error(
        `🔍 [LEVEL-NEW-PACKAGE] ${isEditMode ? 'Update' : 'Create'} error:`,
        error,
      );
      message.error(
        `Failed to ${isEditMode ? 'update' : 'create'} level. Please try again.`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>
        {isEditMode ? 'Edit Level' : 'Create Level'}
      </Title>

      {isLoadingData && (
        <div style={{textAlign: 'center', padding: '50px 0'}}>
          <Spin size="large" tip="Loading level..." />
        </div>
      )}

      {isError && (
        <Alert
          message="Error"
          description="Failed to load level. Please try again."
          type="error"
          showIcon
          style={{marginBottom: 20}}
        />
      )}

      {(!isEditMode || (!isLoadingData && !isError && level)) && (
        <Spin
          spinning={isSaving}
          tip={isEditMode ? 'Updating level...' : 'Creating level...'}>
          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Level Name *
                </Text>
                <Input
                  placeholder="Enter Level Name"
                  onChange={e => setLevelName(e.target.value)}
                  value={levelName}
                  onPressEnter={handleSubmit}
                />
              </div>
            </Col>
          </Row>

          <div style={{margin: 20}}>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={isSaving}
              disabled={isSaving}>
              {isEditMode ? 'Update Level' : 'Create Level'}
            </Button>
            <Button
              style={{marginLeft: 10}}
              onClick={() => history.push('/levels')}
              disabled={isSaving}>
              Cancel
            </Button>
          </div>
        </Spin>
      )}
    </div>
  );
};
